// CookieConsentBanner.tsx
//
// Friendly, non-intrusive cookie consent banner shown on first visit
// to Orbit's own surfaces. Skipped on client-facing studio surfaces
// (portals, public sites, intake forms, booking pages) so a visitor
// landing on a studio's custom domain doesn't see a banner about
// "Orbit's cookies" — that'd be confusing branding-wise.
//
// Consent persists to localStorage. Categories:
//   - essential: always on, can't be disabled (auth, security, core app)
//   - analytics: opt-in (PostHog-style anonymized usage)
//
// To re-open after the user has decided once, dispatch the
// COOKIE_PREFS_EVENT global event (the footer's "Cookie preferences"
// button does this). On re-open the banner shows the detail view
// with their previous choice pre-selected.

import { useState, useEffect } from 'react';
import { Cookie, X, Settings2 } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { isClientFacingSurface } from '@/lib/hostRouting';

const STORAGE_KEY = 'orbit-cookie-consent-v1';

/// Persisted shape. `essential` is `true` literal so the type guarantees
/// it's never turned off — non-essential is opt-in only.
export type CookieConsent = {
  essential: true;
  analytics: boolean;
  decidedAt: string;
};

/// Read current consent (or null if user hasn't decided yet).
export function getStoredConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CookieConsent) : null;
  } catch {
    return null;
  }
}

function setStoredConsent(consent: CookieConsent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // localStorage blocked (private mode / cookies disabled) —
    // banner will show again next visit, which is the safest fallback.
  }
}

/// Dispatch this on the window to re-open the banner — used by the
/// footer's "Cookie preferences" link so a user can revisit their
/// earlier choice.
export const COOKIE_PREFS_EVENT = 'orbit:open-cookie-preferences';

/// Paths where the banner is suppressed. These are surfaces where
/// the visitor isn't really on "Orbit" — they're on a studio's
/// client-facing page (portal, public site, intake form, booking).
/// Showing an Orbit-branded cookie banner there confuses the brand
/// experience the studio is trying to give their own client.
const SUPPRESSED_PATH_PREFIXES = [
  '/portal/',
  '/inquiry-portal/',
  '/embed/',
  '/s/',
  '/sign/',
  '/d/',
  '/book/',
  '/delivery/',
  '/video/',
];

function isSuppressedPath(pathname: string): boolean {
  // SITE-BRAND-1 — also every page on a studio's custom domain and the
  // rest of the client-facing list (proof galleries were missing).
  return (
    SUPPRESSED_PATH_PREFIXES.some((p) => pathname.startsWith(p)) ||
    isClientFacingSurface(pathname)
  );
}

export default function CookieConsentBanner() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [analyticsChoice, setAnalyticsChoice] = useState(true);

  // Show on first visit only — unless someone fires the re-open event
  // (e.g. from the footer's "Cookie preferences" link).
  useEffect(() => {
    if (isSuppressedPath(location.pathname)) return;
    setVisible(getStoredConsent() === null);
  }, [location.pathname]);

  // Reopen when the prefs event fires. Prefill with the existing choice
  // so the toggle reflects what the user picked before.
  useEffect(() => {
    const handler = () => {
      const existing = getStoredConsent();
      if (existing) setAnalyticsChoice(existing.analytics);
      setShowDetail(true);
      setVisible(true);
    };
    window.addEventListener(COOKIE_PREFS_EVENT, handler);
    return () => window.removeEventListener(COOKIE_PREFS_EVENT, handler);
  }, []);

  if (!visible) return null;
  if (isSuppressedPath(location.pathname)) return null;

  const persist = (analytics: boolean) => {
    setStoredConsent({
      essential: true,
      analytics,
      decidedAt: new Date().toISOString(),
    });
    setVisible(false);
    setShowDetail(false);
  };

  // LAUNCH-5 — the ✕ used to hide the banner only until the next page:
  // the first effect re-runs on every route change and, with nothing
  // stored, brought it straight back. Closing without choosing now
  // counts as essential-only (the privacy-first answer) and is
  // remembered. Re-opened from "Cookie preferences" with a choice
  // already stored, ✕ just closes and keeps that choice.
  const close = () => {
    if (getStoredConsent()) {
      setVisible(false);
      setShowDetail(false);
    } else {
      persist(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      // LAUNCH-5 — googie.css lifts the banner above the Aperture bar
      // (it sat on top of the bar's bottom navigation on phones).
      data-cookie-consent=""
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-50 rounded-2xl border border-border bg-background/95 shadow-2xl"
    >
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <OrbitIcon name="circle.grid.2x2" fallback={Cookie} className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg font-semibold text-foreground tracking-tight">
              A quick word on cookies
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
              We use the necessary cookies to keep Orbit signed in and
              running. With your permission, we'll also use analytics
              cookies to see which features actually get used so we
              know what to improve. No ads, no selling your data — read
              the{' '}
              <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link to="/cookies" className="underline underline-offset-2 hover:text-foreground">
                Cookie Policy
              </Link>
              .
            </p>

            {showDetail && (
              <div className="mt-4 space-y-3 border-t border-border pt-3">
                <ConsentRow
                  title="Essential"
                  description="Sign-in, security, and the things Orbit needs to work. Always on."
                  checked
                  disabled
                  onChange={() => {}}
                />
                <ConsentRow
                  title="Analytics"
                  description="Anonymized usage data so we can improve Orbit."
                  checked={analyticsChoice}
                  onChange={setAnalyticsChoice}
                />
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={() => persist(true)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Accept all
              </Button>
              <Button size="sm" variant="outline" onClick={() => persist(false)}>
                Reject non-essential
              </Button>
              {showDetail ? (
                <Button size="sm" variant="ghost" onClick={() => persist(analyticsChoice)}>
                  Save my choices
                </Button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowDetail(true)}
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-2 py-1"
                >
                  <OrbitIcon name="slider.horizontal.3" fallback={Settings2} className="w-3 h-3" />
                  Customize
                </button>
              )}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close cookie banner"
            onClick={close}
            className="flex-shrink-0 -mr-1 -mt-1 p-1 text-muted-foreground hover:text-foreground"
          >
            <OrbitIcon name="xmark" fallback={X} className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ConsentRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground">{title}</div>
        <div className="text-xs text-muted-foreground leading-snug">{description}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={[
          'relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors',
          checked ? 'bg-primary' : 'bg-muted',
          disabled ? 'opacity-50 cursor-not-allowed' : '',
        ].join(' ')}
      >
        <span
          className={[
            'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-[18px]' : 'translate-x-0.5',
          ].join(' ')}
        />
      </button>
    </div>
  );
}
